import { TableCell, TableRow } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import useUnitProvider from '/@/core/context/unit';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { parseUserNode, useInvitationByUser } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getUser } from '/@/store/session/selectors';
import type { Grower } from '/@/types/growers';
import { reverse } from '/@/utils/router';
import NextVisitModal from '/@/views/dashboard/grower/components/next-visit-modal';
import GrowersListActions from '../../../../growers-list-actions';
import GrowerNameCell from '../../../components/grower-name-cell';

const RecommendationsGrowerRow = ({
    grower,
    onGrowerDeleteSuccess,
    dataTestId,
}: {
    grower: Grower;
    onGrowerDeleteSuccess: (growerName: string) => void;
    dataTestId?: string;
}) => {
    const navigate = useNavigate();
    const units = useUnitProvider();
    const user = useSelector(getUser);

    // Transform API data to component format
    const transformGrowerData = useCallback(
        (grower: Grower) => {
            const parsedUserNode = parseUserNode(grower, user);
            const fields = grower.fields || {};
            const totalFields = fields.count || 0;

            // Generate suggestions
            const fieldviewConnected = grower.user?.authProviders?.includes('fieldview');

            return {
                id: grower.id,
                name: parsedUserNode.isSelf ? _('Mi cuenta') : parsedUserNode.name,
                location: parsedUserNode.location,
                totalFields,
                totalArea: convertAreaFromM2(grower?.fields.totalArea || 0, units).toString(),
                fieldviewConnected: fieldviewConnected,
                hasAssignedUser: grower.user !== null,
                totalHybridRecommendations:
                    (grower.recommendationStatistics.countByType?.corn || 0) +
                    (grower.recommendationStatistics.countByType?.soy || 0) +
                    (grower.recommendationStatistics.countByType?.density || 0),
                totalCropProtectionRecommendations:
                    (grower.recommendationStatistics.countByType?.funguicide || 0) +
                    (grower.recommendationStatistics.countByType?.insecticide || 0) +
                    (grower.recommendationStatistics.countByType?.herbicide || 0),
                totalFieldsWithRecommendations: grower.recommendationStatistics.fieldsCount,
                phone: parsedUserNode.phone,
                email: parsedUserNode.email,
                isSelf: parsedUserNode.isSelf,
            };
        },
        [units, user]
    );

    const transformedGrower = useMemo(() => transformGrowerData(grower), [grower, transformGrowerData]);

    const [openNextVisitModal, setOpenNextVisitModal] = useState(false);

    const { invitationStatus } = useInvitationByUser({
        userNodeId: grower.id,
    });

    return (
        <TableRow
            key={transformedGrower.id}
            sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
            data-testid={dataTestId}
            onClick={() => {
                navigate(
                    reverse('Growers.detail', {
                        params: {
                            growerNodeId: transformedGrower.id,
                        },
                        qs: {
                            tab: 'recommendations',
                        },
                    })
                );
            }}
        >
            <TableCell width='25%'>
                <GrowerNameCell
                    name={transformedGrower.name}
                    location={transformedGrower.location}
                    hasAssignedUser={transformedGrower.hasAssignedUser}
                    invitationStatus={invitationStatus}
                />
            </TableCell>
            <TableCell width='25%'>
                <TableTemplateCell
                    title={_('{{count}} Lote', { count: transformedGrower.totalFieldsWithRecommendations })}
                />
            </TableCell>
            <TableCell width='25%'>
                <TableTemplateCell
                    title={_('{{count}} Recomendación', { count: transformedGrower.totalHybridRecommendations })}
                />
            </TableCell>
            <TableCell width='25%'>
                <TableTemplateCell
                    title={_('{{count}} Recomendación', {
                        count: transformedGrower.totalCropProtectionRecommendations,
                    })}
                />
            </TableCell>
            <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                <GrowersListActions
                    growerNodeId={transformedGrower.id}
                    editable={!transformedGrower.isSelf}
                    growerPhone={transformedGrower.phone || ''}
                    growerName={transformedGrower.name}
                    tab='recommendations'
                    onGrowerDeleteSuccess={onGrowerDeleteSuccess}
                    deletable={!transformedGrower.hasAssignedUser}
                />
            </TableCell>
            {openNextVisitModal && (
                <NextVisitModal
                    open={openNextVisitModal}
                    onClose={(e) => {
                        e.stopPropagation();
                        setOpenNextVisitModal(false);
                    }}
                    growerName={transformedGrower.name}
                    growerPhone={transformedGrower.phone}
                />
            )}
        </TableRow>
    );
};

export default RecommendationsGrowerRow;
